// src/Components/dashboard/ContentAnalyticsPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend,
} from 'recharts';
import {
  FileText, Heart, MessageSquare, Eye, TrendingUp,
  Award, ArrowLeft, ChevronLeft, ChevronRight, Briefcase, ShoppingBag,
} from 'lucide-react';
import { dashboardAPI } from '../../services/api';
import { createDashboardRealtimeClient } from '../../services/analyticsRealtime';
import { useTimeRange } from '../layout/DashboardLayout';
import {
  fmt, Skeleton, Card, PageHeader, EmptyState,
  RankBadge, RefreshBtn, ExportBtn, CHART_COLORS,
} from '../dashboard/design-system';

const PAGE_SIZE = 8;

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 border border-white/10 rounded-xl px-3 py-2.5 shadow-xl text-xs">
      <p className="text-white/50 mb-1.5">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-white/70">{p.name}:</span>
          <span className="text-white font-semibold">{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const KPICard = ({ title, value, sub, icon: Icon, accent, loading }) => (
  <Card className="p-5">
    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: `${accent}18` }}>
      <Icon size={20} style={{ color: accent }} />
    </div>
    {loading ? <Skeleton className="h-8 w-24 mb-1" /> : (
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
    )}
    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mt-0.5">{title}</p>
    <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
  </Card>
);

const ComingSoonCard = ({ title, icon: Icon, accent }) => (
  <Card className="p-5 relative overflow-hidden">
    <span className="absolute top-3 right-3 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500">
      Coming Soon
    </span>
    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: `${accent}18` }}>
      <Icon size={20} style={{ color: accent }} />
    </div>
    <p className="text-2xl font-bold text-gray-300 dark:text-gray-600">—</p>
    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mt-0.5">{title}</p>
    <p className="text-xs text-gray-400 mt-0.5">Not available yet</p>
  </Card>
);

// ─── Creator detail view ──────────────────────────────────────────────────────
function CreatorDetail({ creator, posts, onBack }) {
  const metrics = [
    { label: 'Posts',          value: creator.posts,                          color: 'bg-sky-50 text-sky-700 dark:bg-sky-900/20 dark:text-sky-400'        },
    { label: 'Total Likes',    value: fmt(creator.totalLikes),                color: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'          },
    { label: 'Total Comments', value: fmt(creator.totalComments),             color: 'bg-violet-50 text-violet-700 dark:bg-violet-900/20 dark:text-violet-400'},
    { label: 'Total Engagement', value: fmt(creator.engagement),              color: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'},
  ];
  return (
    <div className="space-y-6">
      <button onClick={onBack}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 transition">
        <ArrowLeft size={16} /> Back to Content Analytics
      </button>

      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-7 text-white">
        <div className="flex items-center gap-5">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-2xl font-black">
            {(creator.displayName || creator.authorId || '?').substring(0, 2).toUpperCase()}
          </div>
          <div>
            <h2 className="text-xl font-bold">{creator.displayName || `Author ${creator.authorId?.substring(0, 12)}`}</h2>
            {creator.email && <p className="text-white/50 text-sm mt-0.5">{creator.email}</p>}
            <div className="flex gap-4 mt-3 text-sm text-white/70">
              <span><strong className="text-white">{creator.posts}</strong> posts</span>
              <span><strong className="text-white">{fmt(creator.totalLikes)}</strong> likes</span>
              <span><strong className="text-white">{fmt(creator.engagement)}</strong> engagement</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {metrics.map(m => (
          <div key={m.label} className={`rounded-xl p-4 ${m.color}`}>
            <p className="text-xs font-medium opacity-70">{m.label}</p>
            <p className="text-2xl font-bold mt-1">{m.value}</p>
          </div>
        ))}
      </div>

      <Card className="p-6">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Posts by this creator</h3>
        {posts.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-400 border-b border-gray-100 dark:border-gray-800 pb-2">
                <th className="pb-3 font-semibold">Title</th>
                <th className="pb-3 text-center font-semibold">Likes</th>
                <th className="pb-3 text-center font-semibold">Comments</th>
                <th className="pb-3 text-center font-semibold">Engagement</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
              {posts.map(p => (
                <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                  <td className="py-3 font-medium max-w-xs truncate">{p.title || 'Untitled'}</td>
                  <td className="py-3 text-center text-sm">{fmt(p.likes)}</td>
                  <td className="py-3 text-center text-sm">{fmt(p.comments)}</td>
                  <td className="py-3 text-center font-bold text-red-600">{fmt(p.totalEngagement)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState icon={FileText} title="No post data for this creator" />
        )}
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
export default function ContentAnalyticsPage() {
  const navigate             = useNavigate();
  const [searchParams]       = useSearchParams();
  const { timeRange }        = useTimeRange();

  const [loading,  setLoading]  = useState(true);
  const [data,     setData]     = useState(null);
  const [error,    setError]    = useState(null);
  const [realtime, setRealtime] = useState(false);
  const [page,     setPage]     = useState(1);
  const [selectedCreator, setSelectedCreator] = useState(searchParams.get('creator') || null);
  const [showAllCreators, setShowAllCreators] = useState(searchParams.get('view') === 'creators');
  const [lastUpdated, setLastUpdated] = useState(null);

  const applySnapshot = useCallback((snapshot) => {
    const con = snapshot?.content   || {};
    const ov  = snapshot?.overview  || {};
    const ai  = snapshot?.ai        || {};
    const normalizeDist = (arr) => (arr || []).map(d => ({
      label: d.label ?? d.type ?? d.status ?? d.name ?? 'Unknown',
      value: d.value ?? d.count ?? d.total ?? 0,
    }));
    setData({
      ...con,
      allCreators: snapshot?.topCreators || [],
      // Traction metrics for this page
      postsCreated:     con.totalPosts     ?? ov.totalPosts    ?? 0,
      businessListings: 0,  // needs businesses table
      productListings:  0,  // needs products table
      // Distribution data for charts
      byStatus:   normalizeDist(con.contentDistribution?.byStatus),
      byType:     normalizeDist(con.contentDistribution?.byType),
      byCategory: normalizeDist(con.contentDistribution?.byCategory),
    });
    setLastUpdated(new Date());
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await dashboardAPI.getDashboardSnapshot(timeRange, 50);
      if (res?.data) applySnapshot(res.data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [applySnapshot, timeRange]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [timeRange]);

  useEffect(() => {
    const c = createDashboardRealtimeClient({
      range: timeRange,
      onSnapshot: p => { if (p?.data) { applySnapshot(p.data); setRealtime(true); } },
      onUpdate:   p => { if (p?.data) applySnapshot(p.data); },
      onError:    () => setRealtime(false),
    });
    c.requestSnapshot();
    return () => c.disconnect();
  }, [applySnapshot, timeRange]);

  const exportCSV = () => {
    if (!data) return;
    const csv = [
      ['Beetroot – Content Analytics', new Date().toLocaleString(), timeRange],
      [],
      ['Metric', 'Value'],
      ['Total Posts',           data.totalPosts         || 0],
      ['Published',             data.publishedPosts      || 0],
      ['Draft',                 data.draftPosts          || 0],
      ['Total Likes',           data.totalLikes          || 0],
      ['Total Comments',        data.totalComments       || 0],
      ['Total Views',           data.totalViews          || 0],
      ['Business Listings',     data.businessListings    || 0],
      ['Product Listings',      data.productListings     || 0],
      [],
      ['#', 'Title', 'Type', 'Status', 'Likes', 'Comments', 'Views', 'Engagement'],
      ...(data.topPosts || []).map((p, i) => [i + 1, p.title || 'Untitled', p.type || '', p.status || '', p.likes, p.comments, p.views, p.totalEngagement]),
    ].map(r => r.join(',')).join('\n');
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
      download: `content-${timeRange}-${new Date().toISOString().split('T')[0]}.csv`,
    });
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  // Creator detail
  const creatorData  = selectedCreator ? data?.allCreators?.find(c => c.authorId === selectedCreator) : null;
  const creatorPosts = creatorData ? (data?.topPosts || []).filter(p => p.author === selectedCreator) : [];

  if (selectedCreator) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6">
        {creatorData
          ? <CreatorDetail creator={creatorData} posts={creatorPosts} onBack={() => setSelectedCreator(null)} />
          : <div className="text-center py-20 text-gray-400">
              Creator not found. <button onClick={() => setSelectedCreator(null)} className="text-red-500 underline">Go back</button>
            </div>
        }
      </div>
    );
  }

  // All creators list
  if (showAllCreators) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6 space-y-6">
        <div className="flex items-center gap-4">
          <button onClick={() => setShowAllCreators(false)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 transition">
            <ArrowLeft size={16} /> Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">All Creators</h1>
            <p className="text-sm text-gray-500">{data?.allCreators?.length || 0} creators ranked by engagement</p>
          </div>
        </div>
        <Card className="overflow-hidden">
          <div className="divide-y divide-gray-50 dark:divide-gray-800">
            {(data?.allCreators || []).map((c, i) => (
              <button key={c.authorId}
                onClick={() => { setShowAllCreators(false); setSelectedCreator(c.authorId); }}
                className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 text-left transition group">
                <RankBadge rank={i + 1} />
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {(c.displayName || c.authorId || '?').substring(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 dark:text-gray-100 truncate">{c.displayName || `Author ${c.authorId?.substring(0, 16)}`}</p>
                  <p className="text-xs text-gray-400">{c.posts} posts · {fmt(c.totalLikes)} likes · {fmt(c.totalComments)} comments</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-red-600">{fmt(c.engagement)}</p>
                  <p className="text-xs text-gray-400">engagement</p>
                </div>
                {i < 3 && <Award size={16} className="text-amber-400 flex-shrink-0" />}
              </button>
            ))}
          </div>
        </Card>
      </div>
    );
  }

  // ── Main view ───────────────────────────────────────────────────────────────
  const posts      = data?.topPosts || [];
  const totalPages = Math.ceil(posts.length / PAGE_SIZE);
  const pagedPosts = posts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6 space-y-6">

      <PageHeader title="Content Analytics" sub={`Posts, engagement & listings · ${timeRange}`}
        icon={FileText} lastUpdated={lastUpdated} realtime={realtime}>
        <RefreshBtn onClick={fetchData} loading={loading} />
        <ExportBtn onClick={exportCSV} />
      </PageHeader>

      {error && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 rounded-xl px-4 py-3 text-sm">
          {error} — <button onClick={fetchData} className="underline font-medium">retry</button>
        </div>
      )}

      {/* ── KPI row 1 — post metrics ──────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {loading ? [...Array(5)].map((_, i) => <Card key={i} className="p-5"><Skeleton className="h-8 w-full mb-2" /><Skeleton className="h-4 w-3/4" /></Card>) : (
          <>
            <KPICard title="Total Posts"    value={fmt(data?.totalPosts)}         sub="All content"    icon={FileText}      accent="#7C3AED" loading={loading} />
            <KPICard title="Total Likes"    value={fmt(data?.totalLikes)}         sub="Reactions"      icon={Heart}         accent="#C8102E" loading={loading} />
            <KPICard title="Total Comments" value={fmt(data?.totalComments)}      sub="Discussions"    icon={MessageSquare} accent="#0EA5E9" loading={loading} />
            <KPICard title="Total Views"    value={fmt(data?.totalViews)}         sub="Impressions"    icon={Eye}           accent="#10B981" loading={loading} />
            <KPICard title="Avg Engagement" value={fmt(data?.avgEngagementPerPost)} sub="Per post"     icon={TrendingUp}    accent="#F59E0B" loading={loading} />
          </>
        )}
      </div>

      {/* ── KPI row 2 — traction: listings ───────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard title="Posts Created"      value={fmt(data?.postsCreated)}      sub={`In last ${timeRange}`}  icon={FileText}      accent="#7C3AED" loading={loading} />
        <KPICard title="Published Posts"    value={fmt(data?.publishedPosts)}    sub="Live on platform"        icon={FileText}      accent="#10B981" loading={loading} />
        <ComingSoonCard title="Business Listings"   icon={Briefcase}   accent="#0EA5E9" />
        <ComingSoonCard title="Products & Services" icon={ShoppingBag} accent="#F59E0B" />
      </div>

      {/* ── Distribution charts ───────────────────────────────────────────── */}
      {!loading && (data?.byStatus?.length > 0 || data?.byType?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data.byStatus?.length > 0 && (
            <Card className="p-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-5">By Status</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.byStatus} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="value" name="Posts" radius={[6, 6, 0, 0]}>
                    {data.byStatus.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}
          {data.byType?.length > 0 && (
            <Card className="p-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-5">By Type</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={data.byType} dataKey="value" nameKey="label"
                    cx="50%" cy="50%" outerRadius={80} innerRadius={45}>
                    {data.byType.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Legend verticalAlign="bottom" height={36} iconSize={8}
                    wrapperStyle={{ fontSize: 11 }}
                    formatter={(value, entry) => {
                      const item = data.byType.find(d => d.label === value);
                      const total = data.byType.reduce((s, d) => s + d.value, 0);
                      const pct = total > 0 ? Math.round((item.value / total) * 100) : 0;
                      return `${value} ${pct}%`;
                    }} />
                  <Tooltip
                    contentStyle={{ background: '#111', border: '1px solid #222', borderRadius: 12, fontSize: 12 }}
                    labelStyle={{ color: '#9ca3af' }}
                    itemStyle={{ color: '#e5e7eb' }}
                    formatter={(v, n) => [fmt(v), n]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          )}
        </div>
      )}

      {/* ── Top posts table ───────────────────────────────────────────────── */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-semibold text-gray-900 dark:text-white">Top Performing Posts</h3>
          <span className="text-xs text-gray-400">{posts.length} posts</span>
        </div>

        {loading ? (
          <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
        ) : posts.length === 0 ? (
          <EmptyState icon={FileText} title="No posts found" sub="Posts appear here once they have engagement" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="text-left text-xs text-gray-400 border-b border-gray-100 dark:border-gray-800">
                    <th className="pb-3 w-8 font-semibold uppercase tracking-wide">#</th>
                    <th className="pb-3 font-semibold uppercase tracking-wide">Title</th>
                    <th className="pb-3 text-center font-semibold uppercase tracking-wide hidden md:table-cell">Type</th>
                    <th className="pb-3 text-center font-semibold uppercase tracking-wide hidden md:table-cell">Status</th>
                    <th className="pb-3 text-center font-semibold uppercase tracking-wide">Likes</th>
                    <th className="pb-3 text-center font-semibold uppercase tracking-wide">Cmnts</th>
                    <th className="pb-3 text-center font-semibold uppercase tracking-wide">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-800/50">
                  {pagedPosts.map((p, i) => {
                    const rank = (page - 1) * PAGE_SIZE + i + 1;
                    return (
                      <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors cursor-pointer group">
                        <td className="py-3">
                          <RankBadge rank={rank} />
                        </td>
                        <td className="py-3 font-medium max-w-xs">
                          <p className="truncate group-hover:text-red-600 transition-colors">{p.title || 'Untitled'}</p>
                        </td>
                        <td className="py-3 text-center hidden md:table-cell">
                          <span className="px-2 py-0.5 bg-sky-50 dark:bg-sky-900/20 text-sky-600 dark:text-sky-400 text-xs rounded-full font-medium">{p.type || '—'}</span>
                        </td>
                        <td className="py-3 text-center hidden md:table-cell">
                          <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                            p.status === 'published'
                              ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-500'
                          }`}>{p.status || '—'}</span>
                        </td>
                        <td className="py-3 text-center text-sm font-medium">{fmt(p.likes)}</td>
                        <td className="py-3 text-center text-sm font-medium">{fmt(p.comments)}</td>
                        <td className="py-3 text-center">
                          <span className="font-bold text-red-600">{fmt(p.totalEngagement)}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-100 dark:border-gray-800">
                <p className="text-xs text-gray-400">
                  {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, posts.length)} of {posts.length}
                </p>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-red-400 disabled:opacity-30 transition">
                    <ChevronLeft size={14} />
                  </button>
                  {[...Array(Math.min(totalPages, 7))].map((_, n) => (
                    <button key={n} onClick={() => setPage(n + 1)}
                      className={`w-7 h-7 rounded-lg text-xs font-semibold transition ${
                        page === n + 1 ? 'bg-red-600 text-white' : 'border border-gray-200 dark:border-gray-700 hover:border-red-400'
                      }`}>{n + 1}</button>
                  ))}
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                    className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-red-400 disabled:opacity-30 transition">
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* ── Top creators ─────────────────────────────────────────────────── */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-semibold text-gray-900 dark:text-white">Top Creators</h3>
          <button onClick={() => setShowAllCreators(true)}
            className="text-xs font-semibold text-red-600 hover:text-red-700 transition">
            View all ({data?.allCreators?.length || 0}) →
          </button>
        </div>

        {loading ? (
          <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
        ) : !data?.allCreators?.length ? (
          <EmptyState icon={Award} title="No creator data yet" />
        ) : (
          <div className="space-y-1">
            {(data.allCreators || []).slice(0, 5).map((c, i) => (
              <button key={c.authorId}
                onClick={() => setSelectedCreator(c.authorId)}
                className="w-full flex items-center gap-3 p-3.5 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 border border-gray-100 dark:border-gray-800 hover:border-red-200 dark:hover:border-red-900 text-left group transition">
                <RankBadge rank={i + 1} />
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {(c.displayName || c.authorId || '?').substring(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-gray-800 dark:text-gray-100 truncate">
                    {c.displayName || `Author ${c.authorId?.substring(0, 16)}`}
                  </p>
                  <p className="text-xs text-gray-400">{c.posts} posts · {fmt(c.totalLikes)} likes · {fmt(c.totalComments)} comments</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-bold text-red-600">{fmt(c.engagement)}</p>
                  <p className="text-xs text-gray-400">engagement</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}